// API utility functions with error handling and retry logic

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { supabase } from '../config/supabase';
import { createLogger } from './logger';
import type {
  APIConfig,
  APIError,
  APIResponse,
  CategoryStatsAPIResponse,
  StockCategoryAPIResponse,
  StockCategoryListAPIResponse,
  CategorySearchAPIResponse
} from '../types/api';
import type { StockCategoryFilter, CategoryApiOptions } from '../types/stockCategories';

// Extend Axios types to include metadata and retry flag
declare module 'axios' {
  interface AxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
    _retry?: boolean; // Flag to prevent infinite retry loops
  }

  interface AxiosResponse {
    metadata?: {
      responseTime: number;
    };
  }
}

// Default configuration
const DEFAULT_CONFIG: APIConfig = {
  baseURL: process.env.REACT_APP_API_BASE_URL || '', // Empty = use CRA proxy in dev
  apiKey: '', // No longer used - Supabase auth tokens used instead
  timeout: 10000, // 10 seconds
  retryAttempts: 3,
  retryDelay: 1000, // 1 second
};

type ApiLifecycleEvent =
  | {
      type: 'request';
      method?: string;
      url?: string;
      baseURL?: string;
      hasAuth: boolean;
    }
  | {
      type: 'response';
      status: number;
      url?: string;
      responseTimeMs: number;
      dataSize: number;
    }
  | {
      type: 'request-error';
      url?: string;
      message: string;
      status?: number;
    }
  | {
      type: 'response-error';
      url?: string;
      message: string;
      status?: number;
    };

const apiLogger = createLogger('APIClient', { flag: 'API' });

const logLifecycleEvent = (event: ApiLifecycleEvent): void => {
  apiLogger.batch<ApiLifecycleEvent>('lifecycle', event, {
    flushMs: 200,
    header: (entries) => `🌐 [APIClient] ${entries.length} lifecycle event(s)`,
  });
};

class APIClient {
  private axiosInstance: AxiosInstance;
  private config: APIConfig;

  constructor(config: Partial<APIConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    apiLogger.info('Initializing with config', {
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      hasApiKey: !!this.config.apiKey,
      retryAttempts: this.config.retryAttempts
    });
    this.axiosInstance = this.createAxiosInstance();
  }

  private createAxiosInstance(): AxiosInstance {
    const instance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Request interceptor
    instance.interceptors.request.use(
      async (config) => {
        // Add request timestamp
        config.metadata = { startTime: Date.now() };

        // Add Supabase auth token if user is logged in
        try {
          const { data: { session } } = await supabase.auth.getSession();

          if (session?.access_token) {
            config.headers['Authorization'] = `Bearer ${session.access_token}`;
          }
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Unknown Supabase session error';
          apiLogger.throttled(
            'supabase-session-error',
            60000,
            'warn',
            'Could not get Supabase session',
            { message }
          );
        }

        logLifecycleEvent({
          type: 'request',
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          hasAuth: !!config.headers?.['Authorization'],
        });
        return config;
      },
      (error) => {
        logLifecycleEvent({
          type: 'request-error',
          url: error.config?.url,
          message: error.message,
          status: error.response?.status,
        });
        return Promise.reject(this.handleError(error));
      }
    );

    // Response interceptor
    instance.interceptors.response.use(
      (response) => {
        // Add response time to metadata
        const endTime = Date.now();
        const startTime = response.config.metadata?.startTime || endTime;
        response.metadata = { responseTime: endTime - startTime };
        logLifecycleEvent({
          type: 'response',
          status: response.status,
          url: response.config.url,
          responseTimeMs: response.metadata.responseTime,
          dataSize: response.data ? JSON.stringify(response.data).length : 0,
        });
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 errors with automatic token refresh and retry
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Attempt to refresh the session
            const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();

            if (refreshError || !session?.access_token) {
              // Refresh failed - log user out or show error
              apiLogger.error('Token refresh failed', {
                error: refreshError?.message || 'No session returned'
              });
              logLifecycleEvent({
                type: 'response-error',
                status: error.response?.status,
                url: error.config?.url,
                message: 'Token refresh failed',
              });
              return Promise.reject(this.handleError(error));
            }

            // Refresh succeeded - update Authorization header and retry
            originalRequest.headers['Authorization'] = `Bearer ${session.access_token}`;

            // Retry the original request with the new token
            return instance(originalRequest);
          } catch (refreshError) {
            // If refresh throws an error, log and reject
            apiLogger.error('Token refresh exception', {
              error: refreshError instanceof Error ? refreshError.message : 'Unknown error'
            });
            logLifecycleEvent({
              type: 'response-error',
              status: error.response?.status,
              url: error.config?.url,
              message: 'Token refresh exception',
            });
            return Promise.reject(this.handleError(error));
          }
        }

        // For all other errors or if retry already attempted
        logLifecycleEvent({
          type: 'response-error',
          status: error.response?.status,
          url: error.config?.url,
          message: error.message,
        });
        return Promise.reject(this.handleError(error));
      }
    );

    return instance;
  }

  private handleError(error: AxiosError): APIError {
    const apiError: APIError = {
      message: 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR',
      timestamp: new Date().toISOString(),
      requestId: this.generateRequestId(),
    };

    if (error.response) {
      // Server responded with error status
      apiError.code = `HTTP_${error.response.status}`;
      apiError.message = this.sanitizeErrorMessage(
        (error.response.data as any)?.error?.message || (error.response.data as any)?.error || (error.response.data as any)?.message || error.message
      );
      apiError.details = {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
      };
    } else if (error.request) {
      // Request was made but no response received
      apiError.code = 'NETWORK_ERROR';
      apiError.message = 'Network error - please check your connection';
      apiError.details = {
        timeout: error.code === 'ECONNABORTED',
      };
    } else {
      // Something else happened
      apiError.message = this.sanitizeErrorMessage(error.message);
      apiError.code = 'REQUEST_ERROR';
    }

    return apiError;
  }

  private sanitizeErrorMessage(message: string): string {
    if (typeof message !== 'string') {
      message = typeof message === 'object' ? JSON.stringify(message) : String(message);
    }
    if (!message) return 'An error occurred';
    
    // Remove sensitive information and technical details
    const sanitized = message
      .replace(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g, '[IP]') // IP addresses
      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL]') // Email addresses
      .replace(/\b\w{8}-\w{4}-\w{4}-\w{4}-\w{12}\b/g, '[UUID]') // UUIDs
      .replace(/\bBearer\s+\S+/g, '[TOKEN]') // Bearer tokens
      .replace(/\bpassword[=:]\s*\S+/gi, 'password=[REDACTED]'); // Passwords

    return sanitized.length > 200 ? sanitized.substring(0, 200) + '...' : sanitized;
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async retryRequest<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    attempt: number = 1
  ): Promise<AxiosResponse<T>> {
    try {
      return await requestFn();
    } catch (error) {
      const apiError = error as APIError;
      
      // Don't retry on client errors (4xx) or if we've exceeded max attempts
      if (
        attempt >= this.config.retryAttempts ||
        (apiError.details?.status && apiError.details.status >= 400 && apiError.details.status < 500)
      ) {
        throw error;
      }

      // Exponential backoff
      const delay = this.config.retryDelay * Math.pow(2, attempt - 1);
      await this.sleep(delay);

      return this.retryRequest(requestFn, attempt + 1);
    }
  }

  // Generic request method with retry logic
  private async request<T>(config: AxiosRequestConfig): Promise<APIResponse<T>> {
    try {
      const response = await this.retryRequest(() => this.axiosInstance.request<T>(config));
      
      return {
        data: response.data,
        success: true,
        timestamp: new Date().toISOString(),
        requestId: this.generateRequestId(),
      };
    } catch (error) {
      const apiError = error as APIError;
      throw apiError;
    }
  }

  // HTTP method wrappers
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  // Utility methods
  updateConfig(newConfig: Partial<APIConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.axiosInstance = this.createAxiosInstance();
  }

  getConfig(): APIConfig {
    return { ...this.config };
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.get('/live');
      return true;
    } catch {
      return false;
    }
  }
}

// Create and export a singleton instance
export const apiClient = new APIClient();

// Export the class for testing and custom instances
export { APIClient };

// Convenience functions for common operations
export const get = <T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
  return apiClient.get<T>(url, config);
};

export const post = <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
  return apiClient.post<T>(url, data, config);
};

export const put = <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
  return apiClient.put<T>(url, data, config);
};

export const patch = <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
  return apiClient.patch<T>(url, data, config);
};

export const del = <T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
  return apiClient.delete<T>(url, config);
};

// Error handling utilities
export const isAPIError = (error: any): error is APIError => {
  return !!error && typeof error === 'object' && 'code' in error && 'message' in error;
};

export const getErrorMessage = (error: unknown): string => {
  if (isAPIError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export const isNetworkError = (error: APIError): boolean => {
  return error.code === 'NETWORK_ERROR';
};

export const isTimeoutError = (error: APIError): boolean => {
  return error.details?.timeout === true;
};

export const isServerError = (error: APIError): boolean => {
  return error.details?.status >= 500;
};

export const isClientError = (error: APIError): boolean => {
  return error.details?.status >= 400 && error.details?.status < 500;
};

// Migration error handling utilities
export const isMigrationError = (error: APIError): boolean => {
  return error.details?.status === 410;
};

export const getMigrationInfo = (error: APIError): any => {
  if (isMigrationError(error) && error.details?.data) {
    return error.details.data.migration;
  }
  return null;
};

export const handleMigrationError = (error: APIError, defaultCategory: StockCategoryFilter = 'popular'): string => {
  const migrationInfo = getMigrationInfo(error);
  if (migrationInfo) {
    apiLogger.warn('Legacy endpoint accessed', {
      oldEndpoint: migrationInfo.oldEndpoint,
      newEndpoint: migrationInfo.newEndpoint,
      example: migrationInfo.example
    });

    // Attempt to extract category-based endpoint
    if (migrationInfo.newEndpoint) {
      return migrationInfo.newEndpoint.replace('{category}', defaultCategory);
    }
  }
  return '';
};

// Request cancellation utilities
export const createCancelToken = () => {
  return axios.CancelToken.source();
};

export const isCancelledRequest = (error: any): boolean => {
  return axios.isCancel(error);
};

// Specific API functions

/**
 * Fetch hints with category support
 * @param category - Stock category filter (defaults to 'popular' for backward compatibility)
 * @param date - Optional date in YYYY-MM-DD format for historical hints
 * @returns Promise with hints data
 */
export const fetchHints = (category: StockCategoryFilter = 'popular', date?: string): Promise<APIResponse<import('../types/api').HintsAPIResponse>> => {
  const endpoint = date
    ? `/api/${category}/get_hints?date=${date}`
    : `/api/${category}/get_hints`;
  return get<import('../types/api').HintsAPIResponse>(endpoint);
};



/**
 * Fetch daily stock for a specific category
 * @param category - Stock category filter (defaults to 'popular' for backward compatibility)
 * @returns Promise with daily stock data
 */
export const fetchDailyStock = (category: StockCategoryFilter = 'popular'): Promise<APIResponse<import('../types/api').StockAPIResponse>> => {
  return get<import('../types/api').StockAPIResponse>(`/api/${category}/daily-stock`);
};

// Stock Category API Functions

/**
 * Get category statistics (counts per category)
 */
export const fetchCategoryStats = (includeDetails: boolean = false): Promise<CategoryStatsAPIResponse> => {
  const params = new URLSearchParams();
  if (includeDetails) params.append('includeDetails', 'true');

  return apiClient.get<CategoryStatsAPIResponse['data']>(`/api/stocks/categories/stats?${params}`)
    .then(response => ({
      success: response.success,
      data: response.data,
      timestamp: response.timestamp || new Date().toISOString(),
      message: 'Category statistics retrieved successfully'
    }));
};

/**
 * Get stocks by category with pagination
 */
export const fetchStocksByCategory = (
  category: StockCategoryFilter,
  options: CategoryApiOptions = {}
): Promise<StockCategoryListAPIResponse> => {
  const { limit = 50, offset = 0 } = options;
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());

  return apiClient.get<StockCategoryListAPIResponse['data']>(`/api/stocks/category/${category}?${params}`)
    .then(response => ({
      success: response.success,
      data: response.data,
      timestamp: response.timestamp || new Date().toISOString(),
      message: `Stocks in ${category} category retrieved successfully`
    }));
};

/**
 * Get category information for a specific stock
 */
export const fetchStockCategories = (symbol: string): Promise<StockCategoryAPIResponse> => {
  return apiClient.get<StockCategoryAPIResponse['data']>(`/api/stocks/${symbol}/categories`)
    .then(response => ({
      success: response.success,
      data: response.data,
      timestamp: response.timestamp || new Date().toISOString(),
      message: `Categories for ${symbol} retrieved successfully`
    }));
};

/**
 * Search stocks by query with optional category filtering
 */
export const searchStocks = (
  query: string,
  options: CategoryApiOptions = {}
): Promise<CategorySearchAPIResponse> => {
  const { categories, limit = 50 } = options;
  const params = new URLSearchParams();
  params.append('q', query);
  params.append('limit', limit.toString());

  if (categories && categories.length > 0) {
    params.append('categories', categories.join(','));
  }

  return apiClient.get<CategorySearchAPIResponse['data']>(`/api/stocks/search?${params}`)
    .then(response => ({
      success: response.success,
      data: response.data,
      timestamp: response.timestamp || new Date().toISOString(),
      message: `Search results for "${query}" retrieved successfully`
    }));
};

/**
 * Cache utility for category data
 */
class CategoryCache {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes

  set<T>(key: string, data: T, ttl: number = this.defaultTTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }

  clear(): void {
    this.cache.clear();
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  size(): number {
    // Clean expired entries first
    const now = Date.now();
    for (const [key, entry] of Array.from(this.cache.entries())) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
    return this.cache.size;
  }
}

// Create a singleton cache instance
export const categoryCache = new CategoryCache();

/**
 * Cached category API functions
 */
export const fetchCategoryStatsWithCache = async (
  includeDetails: boolean = false,
  forceRefresh: boolean = false
): Promise<CategoryStatsAPIResponse> => {
  const cacheKey = `category-stats-${includeDetails}`;

  if (!forceRefresh) {
    const cached = categoryCache.get<CategoryStatsAPIResponse>(cacheKey);
    if (cached) return cached;
  }

  const data = await fetchCategoryStats(includeDetails);
  categoryCache.set(cacheKey, data, 5 * 60 * 1000); // 5 minutes TTL
  return data;
};

export const fetchStocksByCategoryWithCache = async (
  category: StockCategoryFilter,
  options: CategoryApiOptions = {},
  forceRefresh: boolean = false
): Promise<StockCategoryListAPIResponse> => {
  const { limit = 50, offset = 0 } = options;
  const cacheKey = `stocks-${category}-${limit}-${offset}`;

  if (!forceRefresh) {
    const cached = categoryCache.get<StockCategoryListAPIResponse>(cacheKey);
    if (cached) return cached;
  }

  const data = await fetchStocksByCategory(category, options);
  categoryCache.set(cacheKey, data, 10 * 60 * 1000); // 10 minutes TTL
  return data;
};

export const fetchStockCategoriesWithCache = async (
  symbol: string,
  forceRefresh: boolean = false
): Promise<StockCategoryAPIResponse> => {
  const cacheKey = `stock-categories-${symbol}`;

  if (!forceRefresh) {
    const cached = categoryCache.get<StockCategoryAPIResponse>(cacheKey);
    if (cached) return cached;
  }

  const data = await fetchStockCategories(symbol);
  categoryCache.set(cacheKey, data, 15 * 60 * 1000); // 15 minutes TTL
  return data;
};

/**
 * Migration retry functions - automatically fallback to category-based endpoints
 */
export const withMigrationFallback = {
  /**
   * Fetch hints with automatic migration fallback
   */
  async fetchHints(category: StockCategoryFilter = 'popular'): Promise<APIResponse<import('../types/api').HintsAPIResponse>> {
    try {
      return await fetchHints(category);
    } catch (error) {
      if (isAPIError(error) && isMigrationError(error)) {
        const fallbackEndpoint = handleMigrationError(error, category);
        if (fallbackEndpoint) {
          return await get<import('../types/api').HintsAPIResponse>(fallbackEndpoint);
        }
      }
      throw error;
    }
  },

  /**
   * Fetch daily stock with automatic migration fallback
   */
  async fetchDailyStock(category: StockCategoryFilter = 'popular'): Promise<APIResponse<import('../types/api').StockAPIResponse>> {
    try {
      return await fetchDailyStock(category);
    } catch (error) {
      if (isAPIError(error) && isMigrationError(error)) {
        const fallbackEndpoint = handleMigrationError(error, category);
        if (fallbackEndpoint) {
          return await get<import('../types/api').StockAPIResponse>(fallbackEndpoint);
        }
      }
      throw error;
    }
  }
};

/**
 * Submit game result to backend
 * @param gameResult - Game result data including attempts, win status, solution, etc.
 * @returns Promise with game result response (leaderboard position, new streak)
 */
export interface GameResultSubmission {
  attempts: number;
  isWin: boolean;
  solution: string;
  guesses: string[];
  completedAt: string;
  category?: string;
  timeTakenMs?: number;
  hintsUsed?: number;
  hintTypesUsed?: string[];
  finalGuess?: string;
  deviceId?: string;
  gameDate?: string;
}

export interface GameResultResponse {
  success: boolean;
  leaderboardPosition: number;
  newStreak: number;
  timestamp: string;
}

export const submitGameResult = async (gameResult: GameResultSubmission): Promise<GameResultResponse> => {
  try {
    const response = await post<GameResultResponse>('/api/game-result', gameResult);
    return response.data;
  } catch (error) {
    apiLogger.error('Failed to submit game result', {
      error: getErrorMessage(error),
      gameResult: {
        category: gameResult.category,
        isWin: gameResult.isWin,
        attempts: gameResult.attempts
      }
    });
    // Return a fallback response instead of throwing - game result submission shouldn't block UX
    return {
      success: false,
      leaderboardPosition: 0,
      newStreak: 0,
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Utility functions for category API
 */
export const CategoryAPIUtils = {
  /**
   * Clear all category cache
   */
  clearCache: (): void => {
    categoryCache.clear();
  },

  /**
   * Get cache statistics
   */
  getCacheStats: (): { size: number; keys: string[] } => {
    const keys: string[] = [];
    for (const [key] of Array.from(categoryCache['cache'].entries())) {
      keys.push(key);
    }
    return {
      size: categoryCache.size(),
      keys
    };
  },

  /**
   * Preload category data
   */
  preloadData: async (): Promise<void> => {
    try {
      // Preload stats
      await fetchCategoryStatsWithCache(false, true);

      // Preload popular categories
      const popularCategories: StockCategoryFilter[] = ['popular', 'tech', 'financial'];
      await Promise.all(
        popularCategories.map(category =>
          fetchStocksByCategoryWithCache(category, { limit: 20 }, true)
        )
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown preload failure';
      apiLogger.warn('Failed to preload category data', { message });
    }
  },

  /**
   * Validate category filter
   */
  isValidCategory: (category: string): category is StockCategoryFilter => {
    const validCategories: StockCategoryFilter[] = [
      'popular', 'tech', 'pharma', 'financial', 'consumer',
      'industrial', 'energy', 'wallstreetbets', 'foreign'
    ];
    return validCategories.includes(category as StockCategoryFilter);
  },

  /**
   * Build search params for API calls
   */
  buildSearchParams: (options: CategoryApiOptions): URLSearchParams => {
    const params = new URLSearchParams();

    if (options.query) params.append('q', options.query);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.includeDetails) params.append('includeDetails', 'true');
    if (options.categories && options.categories.length > 0) {
      params.append('categories', options.categories.join(','));
    }

    return params;
  }
};

/**
 * Dev-only function to reset game state for a category
 * Deletes the user's game_plays record from Supabase for the specified category and today's date
 * Only available in development mode
 */
export const devResetGameCategory = async (category: StockCategoryFilter): Promise<void> => {
  if (process.env.NODE_ENV !== 'development') {
    throw new Error('devResetGameCategory is only available in development mode');
  }

  try {
    const response = await del<{ success: boolean }>(`/api/dev/reset-game/${category}`);

    if (!response.data?.success) {
      throw new Error('Failed to reset game category');
    }
  } catch (error) {
    console.error('Error resetting game category:', error);
    throw error;
  }
};
